import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Fab, Box, TextField, IconButton, Typography, Divider,
    Chip, Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import HistoryIcon from '@mui/icons-material/History';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import axios from 'axios';
import { alpha } from '@mui/material/styles';
import { useTheme } from "@mui/material/styles";
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setAIFlowData, setAgentFlowData, clearAgentFlowData } from '../features/chatbot/chatbotSlice';
import { selectDatabase } from '../features/settings/settingsSlice';
import { selectAgentFlowData } from '../features/chatbot/chatbotSlice';
import { SendToCortexAgentStream, CreateCortexThread, CancelCortexRun } from '../services';
import ChatMessageRenderer from './ChatMessageRenderer';
import AgentActivity from './AgentActivity';

const SIDEBAR_WIDTH = 400;
const BRAND = '#04aeea';
const BRAND_DARK = '#0399d1';

const SUGGESTED_PROMPTS = [
    'How many patients have diabetes?',
    'Find patients with depression aged 18–35',
    'Show patients on GLP-1 medications',
    'What are the top diagnoses in this cohort?',
];

const GREETING = "Hello! I'm your RWE AI assistant. Ask me anything about your patient data or how to use the Query Builder.";
const STORAGE_KEY = 'rwe-ai-conversations';
const MAX_SESSIONS = 20;

// Pulls a single top-level string field out of a JSON-ish blob directly via regex,
// bypassing JSON.parse. Used when the surrounding JSON is invalid (e.g. a malformed
// reactflow_json array) but the field itself (agent_sql, sql_statement) is intact —
// losing those over an unrelated syntax error elsewhere would silently break query
// correctness. Returns null if the field isn't found or fails to decode.
const extractJsonStringField = (text, field) => {
    if (!text) return null;
    const match = text.match(new RegExp(`"${field}"\\s*:\\s*"((?:\\\\.|[^"\\\\])*)"`, 's'));
    if (!match) return null;
    try {
        return JSON.parse(`"${match[1]}"`);
    } catch {
        return null;
    }
};

// Structured cohort replies are stored as JSON for the renderer, but should not
// be animated into the visible chat stream. Preserve prose before a fenced
// response; a bare JSON response has no user-facing text to type out.
const getTypewriterText = (text) => {
    const fencedJsonStart = text.search(/\x60\x60\x60json\b/i);
    if (fencedJsonStart >= 0) return text.slice(0, fencedJsonStart);
    return text.trimStart().startsWith('{') ? '' : text;
};

const loadSessions = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch { return []; }
};

// Activity details (reasoning transcripts, generated SQL) can run to many KB per
// turn. They are worth keeping for review but not at the cost of blowing the
// localStorage quota and silently losing chat history, so truncate on the way in.
const MAX_PERSISTED_DETAIL = 2000;

const trimSessionForStorage = (session) => ({
    ...session,
    messages: (session.messages || []).map(m => (
        m.meta?.activity?.length
            ? {
                ...m,
                meta: {
                    ...m.meta,
                    activity: m.meta.activity.map(e => (
                        typeof e.detail === 'string' && e.detail.length > MAX_PERSISTED_DETAIL
                            ? { ...e, detail: e.detail.slice(0, MAX_PERSISTED_DETAIL) + '\n…(truncated)' }
                            : e
                    )),
                },
            }
            : m
    )),
});

const saveSessions = (list) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_SESSIONS).map(trimSessionForStorage)));
    } catch { /* storage quota */ }
};

const generateTitle = (text) => text.length > 52 ? text.slice(0, 49) + '…' : text;

const groupSessionsByDate = (sessions) => {
    const now = Date.now();
    const today = [], yesterday = [], older = [];
    sessions.forEach(s => {
        const days = Math.floor((now - s.lastUpdated) / 86400000);
        if (days === 0) today.push(s);
        else if (days === 1) yesterday.push(s);
        else older.push(s);
    });
    return [
        ...(today.length     ? [{ label: 'Today',     items: today }]     : []),
        ...(yesterday.length ? [{ label: 'Yesterday', items: yesterday }] : []),
        ...(older.length     ? [{ label: 'Previous',  items: older }]     : []),
    ];
};

const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/* ══════════ Activity timeline reducers ══════════
 * Every progress signal the agent emits (lifecycle status, tool calls, SQL,
 * reasoning tokens, cohort materialization) is folded into one ordered list of
 * entries so the UI can render a single coherent feed instead of three parallel
 * ones. Entry shape: { id, kind, label, detail, state }.
 */

let activitySeq = 0;
const nextActivityId = () => `act-${++activitySeq}`;

/** Mark every still-running entry as settled — a new step means the last one finished. */
const settlePrevious = (entries) =>
    entries.map(e => (e.state === 'active' ? { ...e, state: 'done' } : e));

const appendActivity = (entries, entry) => [
    ...settlePrevious(entries),
    { id: nextActivityId(), state: 'active', ...entry },
];

/**
 * Lifecycle statuses supersede one another rather than stacking up: "planning"
 * then "executing_tool" is one step advancing, not two things that happened.
 * Consecutive status entries therefore collapse into the latest label.
 */
const upsertStatus = (entries, label) => {
    const last = entries[entries.length - 1];
    if (last && last.kind === 'status' && last.state === 'active') {
        return [...entries.slice(0, -1), { ...last, label }];
    }
    return appendActivity(entries, { kind: 'status', label });
};

/** Reasoning tokens stream in one at a time; accumulate them into one entry. */
const appendThinking = (entries, token) => {
    const last = entries[entries.length - 1];
    if (last && last.kind === 'thinking' && last.state === 'active') {
        return [...entries.slice(0, -1), { ...last, detail: (last.detail || '') + token }];
    }
    return appendActivity(entries, { kind: 'thinking', label: 'Reasoning', detail: token });
};

/** Build the expandable detail for a tool call, preferring SQL over raw args. */
const toolDetail = (input) => {
    const sqlVal = input?.sql || input?.query;
    if (sqlVal) return sqlVal;
    const keys = Object.keys(input || {}).filter(k => !/^(sql|query|semantic_model)$/i.test(k));
    if (keys.length === 0) return null;
    try {
        return JSON.stringify(Object.fromEntries(keys.map(k => [k, input[k]])), null, 2);
    } catch {
        return null;
    }
};

/** Settle all entries at the end of a run so nothing is left spinning. */
const finalizeActivity = (entries) => settlePrevious(entries);

/**
 * The Lambda prefixes tool-progress labels with an emoji for legacy consumers.
 * The timeline supplies its own icons, so drop any leading non-word characters.
 */
const stripLeadingEmoji = (text) => text.replace(/^[^\p{L}\p{N}]+/u, '');

/* ════════════════════════════════════════════════════════ */

const Chatbot = ({ user, open, onOpen, onClose }) => {
    const [message, setMessage] = useState('');
    const [conversation, setConversation] = useState([]);
    const [isTyping, setIsTyping] = useState(false);
    const [streamingText, setStreamingText] = useState(null);
    const [activity, setActivity] = useState([]);
    const [runStartedAt, setRunStartedAt] = useState(null);
    const [isStopping, setIsStopping] = useState(false);
    const [cortexThreadId, setCortexThreadId] = useState(0);
    const [sessions, setSessions] = useState(() => loadSessions());
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const cortexThreadIdRef = useRef(0);
    // Cancellation: abortRef tears down the Lambda response stream in the browser,
    // runIdRef lets us also tell Snowflake to stop the run server-side so the
    // agent isn't left burning credits on an answer nobody will see.
    const abortRef = useRef(null);
    const runIdRef = useRef(null);
    const stoppedRef = useRef(false);
    // The ref is the source of truth for the timeline, not the state: the send
    // handler has to read the final entry list the instant the stream resolves,
    // and a setState queued a moment earlier may not have been applied yet.
    const activityRef = useRef([]);
    // parent_message_id for the next threaded turn:
    //   0      – next message starts the thread
    //   number – assistant_message_id of the last reply (per Snowflake Threads docs)
    //   null   – stateless mode (legacy session or thread creation failed): send full history, no thread
    const cortexParentMsgIdRef = useRef(0);
    const currentSessionIdRef = useRef(null);
    const messagesEndRef = useRef(null);

    // Typewriter animation
    const twQueueRef   = useRef('');
    const twCursorRef  = useRef(0);
    const twRafRef     = useRef(null);
    const twResolveRef = useRef(null);
    const CHARS_PER_FRAME = 10;

    const twStep = useCallback(() => {
        const target = twQueueRef.current;
        const cursor = twCursorRef.current;
        const end    = target.length;
        if (cursor >= end) {
            twRafRef.current = null;
            if (twResolveRef.current) { twResolveRef.current(); twResolveRef.current = null; }
            return;
        }
        const next = Math.min(cursor + CHARS_PER_FRAME, end);
        twCursorRef.current = next;
        setStreamingText(target.slice(0, next));
        twRafRef.current = requestAnimationFrame(twStep);
    }, []);

    const typewriterFeed = useCallback((fullText) => {
        twQueueRef.current = fullText;
        if (!twRafRef.current) twRafRef.current = requestAnimationFrame(twStep);
    }, [twStep]);

    const typewriterWait = useCallback(() => new Promise((resolve) => {
        if (!twRafRef.current && twCursorRef.current >= twQueueRef.current.length) resolve();
        else twResolveRef.current = resolve;
    }), []);

    const typewriterReset = useCallback(() => {
        if (twRafRef.current) { cancelAnimationFrame(twRafRef.current); twRafRef.current = null; }
        twQueueRef.current  = '';
        twCursorRef.current = 0;
        twResolveRef.current = null;
    }, []);

    useEffect(() => () => { if (twRafRef.current) cancelAnimationFrame(twRafRef.current); }, []);

    const theme = useTheme();
    const location = useLocation();
    const dispatch = useDispatch();
    const selectedDatabase = useSelector(selectDatabase);
    const agentFlowData = useSelector(selectAgentFlowData);

    const isOnRweAi = location.pathname === '/rwe-ai';

    const isPsychDatabase = !selectedDatabase || selectedDatabase.toLowerCase().includes('psych');

    const DATA_KEYWORDS = [
        'patient', 'diagnos', 'icd', 'medication', 'med ', 'drug', 'lab ', 'labs',
        'vital', 'cohort', 'how many', 'count', 'clinical', 'hospital', 'procedure',
        'cpt', 'insurance', 'demographic', 'age ', 'gender', 'cardio', 'cardiac',
        'heart', 'psych', 'mental', 'condition', 'find me', 'show me', 'get me',
        'filter', 'query', 'database', 'record', 'data', 'select', 'list patients',
        'a1c', 'diabetes', 'depression', 'anxiety', 'glp', 'ozempic', 'blood pressure',
    ];

    const isDataRelatedQuestion = (text) =>
        DATA_KEYWORDS.some(kw => text.toLowerCase().includes(kw));

    useEffect(() => {
        setConversation([{ text: GREETING, sender: 'system', ts: Date.now() }]);
        const id = crypto.randomUUID();
        setCurrentSessionId(id);
        currentSessionIdRef.current = id;
    }, []);

    /** Apply a reducer to the timeline, keeping ref and state in lockstep. */
    const pushActivity = useCallback((reducer) => {
        activityRef.current = reducer(activityRef.current);
        setActivity(activityRef.current);
    }, []);

    const resetActivity = useCallback(() => {
        activityRef.current = [];
        setActivity([]);
    }, []);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [conversation, isTyping, streamingText, activity]);

    // Abort any in-flight run when the sidebar unmounts so the stream doesn't
    // keep writing into a dead component.
    useEffect(() => () => abortRef.current?.abort(), []);

    // Auto-save conversation to localStorage whenever it gains user messages or flow data changes.
    // Both conversation and agentFlowData are in the dep array so the closure is never stale —
    // omitting agentFlowData caused later exchanges (without reactflow_json) to overwrite a
    // diagram that was saved from an earlier exchange in the same session.
    useEffect(() => {
        const userMessages = conversation.filter(c => c.sender === 'user');
        if (userMessages.length === 0 || !currentSessionId) return;

        setSessions(prev => {
            const existingSession = prev.find(s => s.id === currentSessionId);

            // Prefer compact cohort_logic or legacy reactflow_json. If the current
            // exchange lost flow data, keep the previously saved diagram.
            const savedFlowData =
                (agentFlowData?.cohort_logic || agentFlowData?.reactflow_json)
                    ? agentFlowData
                    : existingSession?.agentFlowData ?? agentFlowData ?? null;

            const session = {
                id: currentSessionId,
                threadId: cortexThreadIdRef.current,
                parentMessageId: cortexParentMsgIdRef.current,
                title: generateTitle(userMessages[0].text),
                messages: conversation,
                agentFlowData: savedFlowData,
                lastUpdated: Date.now(),
            };
            const updated = [session, ...prev.filter(s => s.id !== currentSessionId)];
            saveSessions(updated);
            return updated;
        });
    }, [conversation, agentFlowData, currentSessionId]);

    const handleSubmitTicket = async (feedbackType, description, userName) => {
        const url = `https://api.trello.com/1/cards?key=${process.env.REACT_APP_TRELLO_API_KEY}&token=${process.env.REACT_APP_TRELLO_TOKEN}`;
        const formData = new FormData();
        formData.append('idList', '6542b4b35231db341dc06b10');
        formData.append('name', `Feedback: ${feedbackType} from ${userName}`);
        formData.append('desc', description);
        try {
            const response = await axios.post(url, formData);
            console.log({ trelloResponse: response });
        } catch (error) {
            console.error('Error creating card:', error);
        }
    };

    const sendToCortex = async (userMessage) => {
        if (isOnRweAi && !isPsychDatabase && isDataRelatedQuestion(userMessage)) {
            const blockedReply = `I'm currently configured to answer questions about the **IUSM-PSYCH** database only.\n\nYou have **${selectedDatabase}** selected. Patient data queries for this database aren't supported yet.\n\nFeel free to ask me general questions, or switch to an IUSM-PSYCH dataset in the Query Builder to use data-specific features.`;
            setConversation(prev => [
                ...prev,
                { text: userMessage, sender: 'user', ts: Date.now() },
                { text: blockedReply, sender: 'system', ts: Date.now() },
            ]);
            return;
        }

        setConversation(prev => [...prev, { text: userMessage, sender: 'user', ts: Date.now() }]);
        setIsTyping(true);
        setStreamingText('');
        resetActivity();
        setIsStopping(false);
        const startedAt = Date.now();
        setRunStartedAt(startedAt);
        stoppedRef.current = false;
        runIdRef.current = null;
        abortRef.current = new AbortController();
        typewriterReset();

        let contextSuffix = '';
        if (isOnRweAi && selectedDatabase) {
            contextSuffix = `\n\n[Context: User is currently in the RWE AI Query Builder with the "${selectedDatabase}" database selected.]`;
        }

        // ── Server-side threads (Snowflake Cortex Threads API) ─────────────────
        // Lazily create a thread on the first message of a session. Once threaded,
        // Snowflake keeps the conversation context, so each turn sends only the new
        // user message + thread_id + parent_message_id. If creation fails (or the
        // session predates threads), fall back to stateless mode: full history, no thread.
        let threadId = cortexThreadIdRef.current;
        let parentMessageId = cortexParentMsgIdRef.current;

        if (!threadId && parentMessageId !== null) {
            try {
                const created = await CreateCortexThread();
                threadId = created.thread_id;
                parentMessageId = 0;
                setCortexThreadId(threadId);
                cortexThreadIdRef.current = threadId;
                cortexParentMsgIdRef.current = 0;
            } catch (e) {
                console.warn('Cortex thread creation failed — falling back to stateless mode:', e);
                threadId = 0;
                parentMessageId = null;
                cortexParentMsgIdRef.current = null;
            }
        }
        const useThread = Boolean(threadId) && parentMessageId !== null;

        const messages = useThread
            ? [{ role: 'user', content: userMessage + contextSuffix }]
            : [
                ...conversation
                    .filter(c => c.sender === 'user' || c.sender === 'assistant')
                    .map(c => ({ role: c.sender === 'user' ? 'user' : 'assistant', content: c.text })),
                { role: 'user', content: userMessage + contextSuffix },
            ];

        try {
            let fullReply = '';

            const { assistant_message_id, agent_table, cohort_error, aborted } = await SendToCortexAgentStream(
                messages,
                useThread ? threadId : 0,
                useThread ? parentMessageId : 0,
                (chunk) => {
                    fullReply += chunk;
                    typewriterFeed(getTypewriterText(fullReply));
                },
                // Tool-level progress label (e.g. "📊 Querying database…")
                (status) => pushActivity(prev => upsertStatus(prev, stripLeadingEmoji(status))),
                // Structured step — SQL generated by Cortex Analyst
                (icon, text) => pushActivity(prev => appendActivity(prev, {
                    kind: 'sql', label: 'Generated SQL', detail: text,
                })),
                // Extended-thinking reasoning tokens
                (token) => pushActivity(prev => appendThinking(prev, token)),
                // Tool invocation with its full input
                (name, input) => pushActivity(prev => appendActivity(prev, {
                    kind: 'tool', label: name.replace(/_/g, ' '), detail: toolDetail(input),
                })),
                {
                    onRunId: (id) => { runIdRef.current = id; },
                    onAgentStatus: (_code, text) => pushActivity(prev => upsertStatus(prev, text)),
                    abortSignal: abortRef.current.signal,
                },
            );

            // A stopped run keeps whatever text already streamed, but skips the
            // cohort/flow parsing below — a partial answer's JSON is untrustworthy.
            if (aborted) {
                typewriterReset();
                const stoppedActivity = finalizeActivity(activityRef.current);
                // Keep only the prose the user could already see. A half-streamed
                // structured reply is unparseable JSON, and dumping it into the
                // bubble would show raw braces instead of an answer.
                const visibleText = getTypewriterText(fullReply).trim();
                setStreamingText(null);
                resetActivity();
                setConversation(prev => [
                    ...prev,
                    {
                        text: visibleText || '_Stopped before a response was generated._',
                        sender: 'assistant',
                        ts: Date.now(),
                        meta: { activity: stoppedActivity, durationMs: Date.now() - startedAt, stopped: true },
                    },
                ]);
                // Snowflake saves partial output to the thread and reports the
                // assistant_message_id for it via the cancel call; that is handled
                // in handleStop. Without it we cannot safely link the next turn.
                if (useThread && cortexParentMsgIdRef.current === parentMessageId) {
                    cortexParentMsgIdRef.current = null;
                }
                return;
            }

            let flowSentToQueryLogic = false;
            let parsedFlowData = null;
            if (fullReply && isOnRweAi) {
                // Try to match a fenced JSON block first, then fall back to a bare JSON
                // object. agent_sql/agent_table stay in the anchor regex purely to locate
                // the JSON blob — the cohort itself is never read out of it here; only the
                // Lambda's cohort_ready/cohort_error events (agent_table/cohort_error above)
                // are trusted, since only the Lambda proves the table was actually created.
                const jsonBlockMatch = fullReply.match(/```json\s*\n([\s\S]*?)\n?```/) ||
                    fullReply.match(/(\{[\s\S]*"(?:cohort_logic|sql_statement|agent_sql|agent_table)"[\s\S]*\})/);
                if (jsonBlockMatch) {
                    try {
                        const parsed = JSON.parse(jsonBlockMatch[1]);

                        // The cohort is now a server-materialized transient table: the
                        // Cortex Lambda executes the agent's CTAS DDL and reports the
                        // verified table name via a cohort_ready event (or cohort_error
                        // on failure). sql_statement is parsed here only for the
                        // filter-editing UI, not to establish the cohort itself.
                        if (parsed.cohort_logic || parsed.sql_statement || parsed.reactflow_json || parsed.agent_table) {
                            parsedFlowData = {
                                sql_statement: parsed.sql_statement || null,
                                narrative_description: parsed.narrative_description || null,
                                cohort_logic: parsed.cohort_logic || null,
                                reactflow_json: parsed.reactflow_json || null,
                                results_count: parsed.results_count || null,
                                agent_table: agent_table || null,
                                cohort_error: cohort_error || null,
                            };
                            dispatch(setAgentFlowData(parsedFlowData));
                            flowSentToQueryLogic = true;
                        }

                        // Legacy schema: query_filters array
                        if (parsed.query_filters && parsed.query_filters.length > 0) {
                            dispatch(setAIFlowData({ filters: parsed.query_filters }));
                            flowSentToQueryLogic = true;
                        }
                    } catch (e) {
                        // Recover sql_statement independently so malformed presentation
                        // data (e.g. a broken reactflow_json array) doesn't also discard
                        // the filter display. The cohort table needs no recovery here —
                        // it comes only from the Lambda's cohort_ready/cohort_error events.
                        console.warn('Could not parse agent JSON from response:', e, jsonBlockMatch[1]?.slice(0, 500));
                        const recoveredSqlStatement = extractJsonStringField(jsonBlockMatch[1], 'sql_statement');
                        if (recoveredSqlStatement) {
                            console.warn('Recovered sql_statement via fallback extraction; reactflow_json diagram is unavailable for this turn because the surrounding JSON was malformed.');
                            parsedFlowData = {
                                sql_statement: recoveredSqlStatement,
                                narrative_description: null,
                                cohort_logic: null,
                                reactflow_json: null,
                                results_count: null,
                                agent_table: agent_table || null,
                                cohort_error: cohort_error || null,
                            };
                            dispatch(setAgentFlowData(parsedFlowData));
                            flowSentToQueryLogic = true;
                        }
                    }
                } else {
                    // A truncated response may not contain a closing object or code
                    // fence. sql_statement is still worth recovering directly from the
                    // partial text for the filter-editing UI; the cohort table comes
                    // only from the Lambda's cohort_ready/cohort_error events.
                    const recoveredSqlStatement = extractJsonStringField(fullReply, 'sql_statement');
                    if (recoveredSqlStatement) {
                        parsedFlowData = {
                            sql_statement: recoveredSqlStatement,
                            narrative_description: null,
                            cohort_logic: null,
                            reactflow_json: null,
                            results_count: null,
                            agent_table: agent_table || null,
                            cohort_error: cohort_error || null,
                        };
                        dispatch(setAgentFlowData(parsedFlowData));
                        flowSentToQueryLogic = true;
                    }
                }
            }

            // Cohort materialization is the last thing the Lambda does, so it lands
            // at the bottom of the timeline where the user expects the outcome.
            if (cohort_error) {
                pushActivity(prev => appendActivity(prev, {
                    kind: 'cohort', label: 'Cohort could not be built', detail: cohort_error, state: 'error',
                }));
            } else if (agent_table) {
                pushActivity(prev => appendActivity(prev, {
                    kind: 'cohort', label: 'Cohort table ready', detail: agent_table,
                }));
            }

            // Parse and dispatch the compact flow as soon as the complete response
            // arrives. Any short prose prefix may finish animating independently.
            await typewriterWait();

            if (useThread) {
                // Chain the next turn off this reply. If Snowflake didn't return an
                // assistant_message_id, downgrade to stateless mode so follow-ups
                // resend full history rather than mis-linking the thread.
                cortexParentMsgIdRef.current = assistant_message_id ?? null;
            }

            const finishedActivity = finalizeActivity(activityRef.current);
            setStreamingText(null);
            resetActivity();

            if (fullReply && fullReply.trim()) {
                setConversation(prev => [
                    ...prev,
                    {
                        text: fullReply,
                        sender: 'assistant',
                        ts: Date.now(),
                        meta: {
                            flowSentToQueryLogic,
                            agentFlowData: parsedFlowData,
                            activity: finishedActivity,
                            durationMs: Date.now() - startedAt,
                        },
                    },
                ]);
            }
        } catch (error) {
            console.error('Error calling Cortex Agent:', error);
            setStreamingText(null);
            setConversation(prev => [
                ...prev,
                {
                    text: 'Sorry, I encountered an error. Please try again.',
                    sender: 'assistant',
                    ts: Date.now(),
                    meta: { activity: finalizeActivity(activityRef.current), durationMs: Date.now() - startedAt },
                },
            ]);
        } finally {
            setIsTyping(false);
            setIsStopping(false);
            setStreamingText(null);
            resetActivity();
            setRunStartedAt(null);
            abortRef.current = null;
            runIdRef.current = null;
        }
    };

    /**
     * Stop the in-flight run.
     *
     * Two things have to happen: tear down the browser's stream so the UI settles
     * immediately, and tell Snowflake to cancel the run so the agent stops working
     * (and stops billing) rather than running to completion unobserved. Snowflake
     * persists whatever it produced to the thread and hands back the
     * assistant_message_id for that partial turn — using it as the next
     * parent_message_id is what keeps the thread linked after a stop.
     */
    const handleStop = useCallback(async () => {
        if (!isTyping || stoppedRef.current) return;
        stoppedRef.current = true;
        setIsStopping(true);

        const runId = runIdRef.current;
        abortRef.current?.abort();

        if (!runId) return;
        try {
            const { assistant_message_id } = await CancelCortexRun(runId);
            if (assistant_message_id != null) {
                cortexParentMsgIdRef.current = assistant_message_id;
            }
        } catch (e) {
            // The browser stream is already torn down, so the user sees a stopped
            // run either way; a failed server-side cancel only means Snowflake may
            // finish the run on its own.
            console.warn('Cortex run cancel failed:', e);
        }
    }, [isTyping]);

    const startNewChat = useCallback(() => {
        abortRef.current?.abort();
        setConversation([{ text: GREETING, sender: 'system', ts: Date.now() }]);
        setCortexThreadId(0);
        cortexThreadIdRef.current = 0;
        cortexParentMsgIdRef.current = 0;
        setStreamingText(null);
        resetActivity();
        setRunStartedAt(null);
        setShowHistory(false);
        dispatch(clearAgentFlowData());
        const id = crypto.randomUUID();
        setCurrentSessionId(id);
        currentSessionIdRef.current = id;
    }, [dispatch, resetActivity]);

    const loadSession = useCallback((session) => {
        abortRef.current?.abort();
        setConversation(session.messages);
        setCortexThreadId(session.threadId || 0);
        cortexThreadIdRef.current = session.threadId || 0;
        // Sessions saved before server-side threads existed have a threadId but no
        // parentMessageId — resume those in stateless mode (null) so context isn't lost.
        cortexParentMsgIdRef.current = session.parentMessageId ?? (session.threadId ? null : 0);
        setCurrentSessionId(session.id);
        currentSessionIdRef.current = session.id;
        setStreamingText(null);
        resetActivity();
        setRunStartedAt(null);
        setShowHistory(false);

        // Find the best agentFlowData from this session:
        // 1. Prefer the last assistant message that has compact cohort_logic or
        //    legacy reactflow_json (full diagram)
        // 2. Fall back to the last assistant message with any agentFlowData (e.g. sql_statement only)
        // 3. Fall back to the legacy top-level session.agentFlowData field
        const assistantMessages = (session.messages || []).filter(
            m => m.sender === 'assistant' && m.meta?.agentFlowData
        );
        const withDiagram = assistantMessages.filter(m =>
            m.meta.agentFlowData?.cohort_logic || m.meta.agentFlowData?.reactflow_json
        );
        const bestFlowData =
            (withDiagram.length > 0 ? withDiagram[withDiagram.length - 1].meta.agentFlowData : null) ||
            (assistantMessages.length > 0 ? assistantMessages[assistantMessages.length - 1].meta.agentFlowData : null) ||
            session.agentFlowData ||
            null;

        if (bestFlowData) {
            dispatch(setAgentFlowData(bestFlowData));
        } else {
            dispatch(clearAgentFlowData());
        }
    }, [dispatch, resetActivity]);

    const deleteSession = useCallback((e, sessionId) => {
        e.stopPropagation();
        setSessions(prev => {
            const updated = prev.filter(s => s.id !== sessionId);
            saveSessions(updated);
            return updated;
        });
        if (sessionId === currentSessionIdRef.current) startNewChat();
    }, [startNewChat]);

    const handleSendMessage = () => {
        if (message.trim() && !isTyping) {
            sendToCortex(message.trim());
            setMessage('');
        }
    };

    const handlePromptClick = (prompt) => {
        sendToCortex(prompt);
    };

    const showSuggestedPrompts = conversation.length <= 1 && !isTyping && streamingText === null;

    /* ─────────────────────────── render ─────────────────────────── */
    return (
        <>
            {/* ── Sidebar panel ── */}
            <Box sx={{
                width: open ? SIDEBAR_WIDTH : 0,
                minWidth: 0,
                flexShrink: 0,
                overflow: 'hidden',
                transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                height: '100vh',
                borderLeft: open ? '1px solid' : 'none',
                borderColor: 'divider',
                boxShadow: open ? '-6px 0 24px rgba(0,0,0,0.10)' : 'none',
                bgcolor: 'background.default',
            }}>
                <Box sx={{ width: SIDEBAR_WIDTH, height: '100%', display: 'flex', flexDirection: 'column' }}>

                    {/* ── Header ── */}
                    <Box sx={{
                        background: `linear-gradient(135deg, ${BRAND} 0%, #0076b4 100%)`,
                        px: 2,
                        py: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        flexShrink: 0,
                    }}>
                        <Box sx={{
                            width: 34, height: 34, borderRadius: '10px',
                            bgcolor: 'rgba(255,255,255,0.18)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                            <AutoAwesomeIcon sx={{ color: 'white', fontSize: 18 }} />
                        </Box>

                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>
                                RWE AI
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                                <Box sx={{
                                    width: 6, height: 6, borderRadius: '50%', bgcolor: '#69f0ae',
                                    boxShadow: '0 0 0 2px rgba(105,240,174,0.3)',
                                    flexShrink: 0,
                                }} />
                                <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.7rem', lineHeight: 1 }}>
                                    Snowflake Cortex
                                </Typography>
                            </Box>
                        </Box>

                        <Tooltip title="New chat" placement="left">
                            <IconButton
                                onClick={startNewChat}
                                size="small"
                                sx={{ color: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', color: 'white' } }}
                            >
                                <AddCommentOutlinedIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={showHistory ? 'Back to chat' : 'History'} placement="left">
                            <IconButton
                                onClick={() => setShowHistory(h => !h)}
                                size="small"
                                sx={{
                                    color: showHistory ? 'white' : 'rgba(255,255,255,0.85)',
                                    bgcolor: showHistory ? 'rgba(255,255,255,0.2)' : 'transparent',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', color: 'white' },
                                }}
                            >
                                <HistoryIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Close" placement="left">
                            <IconButton
                                onClick={onClose}
                                size="small"
                                sx={{
                                    color: 'rgba(255,255,255,0.85)',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', color: 'white' },
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Divider />

                    {/* ── History panel ── */}
                    {showHistory && (
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <Box sx={{
                                flexGrow: 1,
                                overflowY: 'auto',
                                px: 1,
                                py: 1.25,
                                '&::-webkit-scrollbar': { width: 4 },
                                '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                                '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(4,174,234,0.25)', borderRadius: 4 },
                            }}>
                                {sessions.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', mt: 6 }}>
                                        <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>No previous conversations</Typography>
                                        <Typography sx={{ color: 'text.disabled', fontSize: '0.75rem', mt: 0.5 }}>
                                            Your chats will appear here
                                        </Typography>
                                    </Box>
                                ) : (
                                    groupSessionsByDate(sessions).map(({ label, items }) => (
                                        <Box key={label} sx={{ mb: 1.5 }}>
                                            <Typography sx={{
                                                fontSize: '0.68rem', color: 'text.secondary', fontWeight: 600,
                                                textTransform: 'uppercase', letterSpacing: '0.06em',
                                                px: 1, pb: 0.5,
                                            }}>
                                                {label}
                                            </Typography>
                                            {items.map(session => {
                                                const isActive = session.id === currentSessionId;
                                                const msgCount = session.messages?.filter(m => m.sender === 'user').length ?? 0;
                                                const lastTime = session.lastUpdated ? new Date(session.lastUpdated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                                                return (
                                                <Box
                                                    key={session.id}
                                                    onClick={() => loadSession(session)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        px: 1.25,
                                                        py: 0.9,
                                                        mb: 0.5,
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        bgcolor: isActive ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.08) : 'background.paper',
                                                        border: '1px solid',
                                                        borderColor: isActive ? alpha(theme.palette.primary.main, 0.35) : 'divider',
                                                        boxShadow: isActive ? '0 1px 4px rgba(4,174,234,0.10)' : '0 1px 2px rgba(0,0,0,0.04)',
                                                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.05), borderColor: alpha(theme.palette.primary.main, 0.35) },
                                                        '& .delete-btn': { visibility: 'hidden' },
                                                        '&:hover .delete-btn': { visibility: 'visible' },
                                                        transition: 'background 0.15s ease, border-color 0.15s ease',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Box sx={{
                                                        width: 28, height: 28, borderRadius: '7px', flexShrink: 0, mt: 0.1,
                                                        bgcolor: isActive ? BRAND : 'action.hover',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}>
                                                        <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: isActive ? 'white' : BRAND }} />
                                                    </Box>
                                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                        <Typography sx={{
                                                            fontSize: '0.82rem',
                                                            fontWeight: isActive ? 600 : 400,
                                                            lineHeight: 1.35,
                                                            color: 'text.primary',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            {session.title}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.3 }}>
                                                            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1 }}>
                                                                {msgCount} {msgCount === 1 ? 'message' : 'messages'}
                                                            </Typography>
                                                            {lastTime && (
                                                                <>
                                                                    <Box sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: 'text.disabled', flexShrink: 0 }} />
                                                                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1 }}>
                                                                        {lastTime}
                                                                    </Typography>
                                                                </>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                    <Tooltip title="Delete" placement="left">
                                                        <IconButton
                                                            className="delete-btn"
                                                            size="small"
                                                            onClick={(e) => deleteSession(e, session.id)}
                                                            sx={{
                                                                color: 'text.disabled',
                                                                flexShrink: 0,
                                                                p: 0.4,
                                                                mt: 0.1,
                                                                '&:hover': { color: '#e53e3e', bgcolor: 'rgba(229,62,62,0.08)' },
                                                            }}
                                                        >
                                                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                                );
                                            })}
                                        </Box>
                                    ))
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* ── Messages scroll area ── */}
                    {!showHistory && <Box sx={{
                        flexGrow: 1,
                        overflowY: 'auto',
                        px: 1.75,
                        py: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.25,
                        // Thin custom scrollbar
                        '&::-webkit-scrollbar': { width: 4 },
                        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                            bgcolor: 'rgba(4,174,234,0.25)',
                            borderRadius: 4,
                            '&:hover': { bgcolor: 'rgba(4,174,234,0.45)' },
                        },
                    }}>
                        {/* ── Conversation messages ── */}
                        {conversation.map((msg, index) => {
                            const isUser = msg.sender === 'user';
                            const pastActivity = msg.meta?.activity;
                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: isUser ? 'flex-end' : 'flex-start',
                                        mb: 0.5,
                                    }}
                                >
                                    {/* Replay of what the agent did on this turn — collapsed
                                        by default, reopenable at any time. */}
                                    {!isUser && pastActivity?.length > 0 && (
                                        <Box sx={{ width: '100%', mb: 0.6 }}>
                                            <AgentActivity
                                                entries={pastActivity}
                                                running={false}
                                                durationMs={msg.meta?.durationMs}
                                                stopped={msg.meta?.stopped}
                                            />
                                        </Box>
                                    )}
                                    <Box
                                        sx={{
                                            maxWidth: isUser ? '82%' : '100%',
                                            px: isUser ? 1.5 : 1.75,
                                            py: isUser ? 1 : 1.25,
                                            borderRadius: isUser
                                                ? '18px 18px 4px 18px'
                                                : '4px 18px 18px 18px',
                                            bgcolor: isUser ? BRAND : 'background.paper',
                                            color: isUser ? 'white' : 'text.primary',
                                            boxShadow: isUser
                                                ? `0 2px 8px rgba(4,174,234,0.30)`
                                                : '0 1px 4px rgba(0,0,0,0.07)',
                                            border: isUser ? 'none' : '1px solid',
                                            borderColor: isUser ? 'transparent' : 'divider',
                                            overflowWrap: 'break-word',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {isUser ? (
                                            <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.55, color: 'white' }}>
                                                {msg.text}
                                            </Typography>
                                        ) : (
                                            <ChatMessageRenderer
                                                text={msg.text}
                                                suppressFlow={msg.meta?.flowSentToQueryLogic}
                                            />
                                        )}
                                    </Box>
                                    {msg.ts && (
                                        <Typography sx={{
                                            fontSize: '0.65rem',
                                            color: 'text.disabled',
                                            mt: 0.4,
                                            mx: 0.5,
                                        }}>
                                            {formatTime(msg.ts)}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}

                        {/* ── Suggested prompts (empty state) ── */}
                        {showSuggestedPrompts && (
                            <Box sx={{ mt: 1.5, mb: 0.5 }}>
                                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 1, fontWeight: 500, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                                    Try asking
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    {SUGGESTED_PROMPTS.map((prompt) => (
                                        <Chip
                                            key={prompt}
                                            label={prompt}
                                            onClick={() => handlePromptClick(prompt)}
                                            size="small"
                                            sx={{
                                                alignSelf: 'flex-start',
                                                bgcolor: 'background.paper',
                                                border: '1px solid',
                                                borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.45 : 0.2),
                                                color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#1a6b8a',
                                                fontSize: '0.78rem',
                                                height: 'auto',
                                                py: 0.5,
                                                cursor: 'pointer',
                                                borderRadius: '10px',
                                                '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.4 },
                                                '&:hover': {
                                                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.1),
                                                    borderColor: BRAND,
                                                    color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#0076b4',
                                                },
                                                transition: 'all 0.15s ease',
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* ── Live run: activity timeline, then the answer as it streams ── */}
                        {isTyping && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', mb: 0.5 }}>
                                <Box sx={{ width: '100%' }}>
                                    <AgentActivity
                                        entries={activity}
                                        running
                                        startedAt={runStartedAt}
                                        stopped={isStopping}
                                    />
                                </Box>

                                {streamingText ? (
                                    <Box sx={{
                                        mt: 0.6,
                                        maxWidth: '100%',
                                        px: 1.75,
                                        py: 1.25,
                                        borderRadius: '4px 18px 18px 18px',
                                        bgcolor: 'background.paper',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                                        overflowWrap: 'break-word',
                                        wordBreak: 'break-word',
                                    }}>
                                        <ChatMessageRenderer text={streamingText} suppressFlow />
                                    </Box>
                                ) : null}
                            </Box>
                        )}

                        <div ref={messagesEndRef} />
                    </Box>}

                    {/* ── Input area ── */}
                    {!showHistory && <Box sx={{
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        px: 1.5,
                        pt: 1.25,
                        pb: 1,
                        flexShrink: 0,
                        bgcolor: 'background.paper',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75 }}>
                            <TextField
                                fullWidth
                                multiline
                                maxRows={4}
                                size="small"
                                placeholder="Ask anything about your data…"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '14px',
                                        bgcolor: 'background.default',
                                        fontSize: '0.875rem',
                                        '& fieldset': { borderColor: 'divider' },
                                        '&:hover fieldset': { borderColor: 'primary.light' },
                                        '&.Mui-focused fieldset': { borderColor: BRAND, borderWidth: 1.5 },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1,
                                        lineHeight: 1.5,
                                    },
                                }}
                            />
                            {/* The send control becomes a stop control for the duration of a
                                run — same position, so stopping is exactly where the user
                                just clicked send. */}
                            <Tooltip title={isTyping ? (isStopping ? 'Stopping…' : 'Stop generating') : (message.trim() ? 'Send' : '')} placement="top">
                                <span>
                                    <IconButton
                                        onClick={isTyping ? handleStop : handleSendMessage}
                                        disabled={isTyping ? isStopping : !message.trim()}
                                        aria-label={isTyping ? 'Stop generating' : 'Send message'}
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            mb: 0.25,
                                            bgcolor: isTyping ? 'grey.700' : (message.trim() ? BRAND : 'action.disabledBackground'),
                                            color: isTyping || message.trim() ? 'common.white' : 'text.disabled',
                                            borderRadius: '10px',
                                            transition: 'all 0.15s ease',
                                            '&:hover': {
                                                bgcolor: isTyping ? 'grey.800' : (message.trim() ? BRAND_DARK : 'action.selected'),
                                                transform: isTyping || message.trim() ? 'scale(1.05)' : 'none',
                                            },
                                            '&.Mui-disabled': {
                                                bgcolor: 'action.disabledBackground',
                                                color: 'text.disabled',
                                            },
                                        }}
                                    >
                                        {isTyping
                                            ? <StopRoundedIcon sx={{ fontSize: 20 }} />
                                            : <SendIcon sx={{ fontSize: 17 }} />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                        <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', mt: 0.6, ml: 0.5 }}>
                            {isTyping ? 'Generating… press stop to cancel' : 'Enter to send · Shift+Enter for new line'}
                        </Typography>
                    </Box>}

                </Box>
            </Box>

            {/* ── FAB — only shown when sidebar is closed ── */}
            {!open && (
                <Fab
                    onClick={onOpen}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        background: `linear-gradient(135deg, ${BRAND} 0%, #0076b4 100%)`,
                        color: 'white',
                        boxShadow: 'none',
                        '&:hover': {
                            background: `linear-gradient(135deg, ${BRAND_DARK} 0%, #005f92 100%)`,
                            boxShadow: 'none',
                            transform: 'scale(1.05)',
                        },
                        // Fab's default elevation also fires on press.
                        '&:active': { boxShadow: 'none' },
                        transition: 'all 0.2s ease',
                    }}
                    aria-label="open RWE AI"
                >
                    <AutoAwesomeIcon />
                </Fab>
            )}
        </>
    );
};

export default Chatbot;
